import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  formatPercentWithColor,
  getSignalColor,
  TYPOGRAPHY,
} from '../../theme/forecastStyles';
import type { StockHistoryResponse, StockHistoryStocksResponse } from '../../lib/api/stock-history';

interface SymbolDrilldownProps {
  selectedSymbol: string | 'ALL';
  onSymbolChange: (symbol: string | 'ALL') => void;
  stocks: StockHistoryStocksResponse;
  symbolHistory: StockHistoryResponse;
  symbolLoading: boolean;
  symbolError: string | null;
  formatDate: (dateStr: string | null) => string;
}

export default function SymbolDrilldown({
  selectedSymbol,
  onSymbolChange,
  stocks,
  symbolHistory,
  symbolLoading,
  symbolError,
  formatDate,
}: SymbolDrilldownProps) {
  return (
    <Card sx={{ bgcolor: 'background.default' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Per-Symbol Forecast Timeline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select a symbol to review individual forecasts, outcomes, and returns.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
              Symbol
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedSymbol}
                onChange={(e) => onSymbolChange(e.target.value === 'ALL' ? 'ALL' : e.target.value)}
                sx={{
                  fontSize: TYPOGRAPHY.tiny,
                  bgcolor: 'grey.900',
                  '& .MuiSelect-select': {
                    py: 1,
                    px: 1
                  }
                }}
              >
                <MenuItem value="ALL" sx={{ fontSize: TYPOGRAPHY.tiny }}>All (summary view)</MenuItem>
                {stocks.map((s) => (
                  <MenuItem key={s.symbol} value={s.symbol} sx={{ fontSize: TYPOGRAPHY.tiny }}>
                    {s.symbol}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {selectedSymbol === 'ALL' && (
          <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
            Pick a symbol above to inspect its recent forecasts. This view surfaces hits, misses, and
            pending outcomes without changing any backend APIs.
          </Typography>
        )}

        {selectedSymbol !== 'ALL' && (
          <Box sx={{ mt: 2 }}>
            {symbolLoading && (
              <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                Loading {selectedSymbol} history...
              </Typography>
            )}
            {symbolError && (
              <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'error.main' }}>
                {symbolError}
              </Typography>
            )}

            {!symbolLoading && !symbolError && symbolHistory.length === 0 && (
              <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                No forecast records found yet for {selectedSymbol}.
              </Typography>
            )}

            {!symbolLoading && !symbolError && symbolHistory.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Date</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Signal</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Direction</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>1d</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>3d</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>7d</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {symbolHistory.map((h) => {
                      const one = formatPercentWithColor(h.return_1d);
                      const three = formatPercentWithColor(h.return_3d);
                      const seven = formatPercentWithColor(h.return_7d);

                      return (
                        <TableRow key={`${h.symbol}-${h.date}-${h.signal}`}>
                          <TableCell sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                            {formatDate(h.date)}
                          </TableCell>
                          <TableCell sx={{ fontSize: TYPOGRAPHY.tiny, fontWeight: 500, color: getSignalColor(h.signal) }}>
                            {h.signal}
                          </TableCell>
                          <TableCell sx={{ fontSize: '9px', color: 'text.secondary' }}>
                            {h.direction}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: one.color }}>
                            {one.text}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: three.color }}>
                            {three.text}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: seven.color }}>
                            {seven.text}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
